import React from "react";
import { Link } from "react-router-dom";
import KrionexLogo from "../components/brand/KrionexLogo";

const NETWORK_ICONS = [
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Polygon", logo: "/logos/polygon.png" },
    { name: "Base", logo: "/logos/base.png" },
    { name: "Arbitrum", logo: "/logos/arbitrum.png" },
    { name: "BNB Chain", logo: "/logos/bnb.png" },
    { name: "Optimism", logo: "/logos/optimism.png" },
    { name: "Avalanche", logo: "/logos/avalanche.png" },
];

const CAPABILITIES = [
    {
        title: "Launch",
        desc: "Launch ERC20, ERC721, and ERC1155 contracts through a structured wallet-confirmed workflow.",
    },
    {
        title: "Manage",
        desc: "Run post-launch token actions from a focused operations workspace.",
    },
    {
        title: "Monitor",
        desc: "Keep verification, token activity, liquidity visibility, and deployment history organized.",
    },
];

const WORKFLOW = [
    {
        step: "01",
        title: "Configure",
        desc: "Choose contract type, supply, metadata URI, features, owner settings, and target network.",
    },
    {
        step: "02",
        title: "Confirm",
        desc: "Review the prepared action and approve it from the connected wallet.",
    },
    {
        step: "03",
        title: "Deploy",
        desc: "Launch the contract and store deployment records in the workspace.",
    },
    {
        step: "04",
        title: "Operate",
        desc: "Use operations and history tools to manage activity after launch.",
    },
];

const TRUST_POINTS = [
    {
        title: "Wallet-confirmed execution",
        desc: "Users approve their own contract actions and wallet transactions.",
    },
    {
        title: "Network-aware workflows",
        desc: "Actions are prepared with selected chain and contract context.",
    },
    {
        title: "Activity visibility",
        desc: "Deployment records and contract references stay organized.",
    },
    {
        title: "Clear billing boundary",
        desc: "Krionex access is separate from gas, network, and wallet fees.",
    },
];

function CheckItem({ children }) {
    return (
        <li className="flex gap-3 text-sm leading-6 text-slate-700">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C59B45]" />
            <span>{children}</span>
        </li>
    );
}

function SectionHeader({ eyebrow, title, subtitle, center = false }) {
    return (
        <div className={center ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}>
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-[#A87E2C]">
                {eyebrow}
            </div>
            <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] text-[#050713] sm:text-4xl">
                {title}
            </h2>
            {subtitle ? (
                <p className="mt-4 text-base leading-8 text-slate-600">{subtitle}</p>
            ) : null}
        </div>
    );
}

export default function Landing() {
    return (
        <div className="min-h-screen overflow-hidden bg-[#F7F8FA] text-[#050713]">
            <div className="pointer-events-none fixed inset-0 overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_6%,rgba(197,155,69,0.12),transparent_28%),radial-gradient(circle_at_92%_10%,rgba(15,23,42,0.08),transparent_26%),radial-gradient(circle_at_70%_86%,rgba(79,70,229,0.06),transparent_34%)]" />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.88),rgba(247,248,250,0.96)_38%,rgba(247,248,250,1))]" />
            </div>

            <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/85 backdrop-blur-2xl">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
                    <KrionexLogo
                        variant="wordmark"
                        imageClassName="h-14 w-auto object-contain"
                    />

                    <nav className="hidden items-center gap-8 text-sm font-medium text-slate-600 lg:flex">
                        <a href="#platform" className="transition hover:text-[#050713]">
                            Platform
                        </a>
                        <a href="#workflow" className="transition hover:text-[#050713]">
                            Workflow
                        </a>
                        <a href="#security" className="transition hover:text-[#050713]">
                            Security
                        </a>
                        <Link to="/guide" className="transition hover:text-[#050713]">
                            Guide
                        </Link>
                        <a href="#pricing" className="transition hover:text-[#050713]">
                            Pricing
                        </a>
                    </nav>

                    <div className="flex items-center gap-3">
                        <Link
                            to="/upgrade"
                            className="hidden rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 md:inline-flex"
                        >
                            View plans
                        </Link>
                        <Link
                            to="/studio"
                            className="inline-flex rounded-xl bg-[#050713] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(15,23,42,0.18)] transition hover:-translate-y-0.5 hover:bg-slate-900"
                        >
                            Deploy Free
                        </Link>
                    </div>
                </div>
            </header>

            <main className="relative z-10">
                <section className="mx-auto max-w-7xl px-6 pb-20 pt-16 md:pb-28 md:pt-24">
                    <div className="grid items-center gap-14 lg:grid-cols-[1.02fr_0.98fr]">
                        <div>
                            <div className="inline-flex items-center rounded-full border border-[#C59B45]/30 bg-[#FFF8E1] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8A6A1F] shadow-sm">
                                Browser-based · Non-custodial · No setup required
                            </div>

                            <h1 className="mt-7 max-w-4xl text-5xl font-semibold leading-[1.02] tracking-[-0.065em] text-[#050713] sm:text-6xl lg:text-7xl">
                                Deploy and manage token, NFT, multi-token, and supported custom contracts from one workspace.
                            </h1>

                            <p className="mt-6 max-w-2xl text-base leading-8 text-slate-600 sm:text-lg">
                                No scripts. No local setup. Connect your wallet, configure your ERC20, ERC721, ERC1155, or supported custom Solidity contract, and deploy through a wallet-signed flow. Krionex helps with deployment records, verification status, dashboards, ownership visibility, token actions, and post-launch operations. You pay blockchain gas directly from your wallet. Krionex never holds your funds or private keys.
                            </p>

                            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                                <Link
                                    to="/studio"
                                    className="inline-flex items-center justify-center rounded-xl bg-[#050713] px-6 py-3.5 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(15,23,42,0.22)] transition hover:-translate-y-0.5 hover:bg-slate-900"
                                >
                                    Deploy Your First Contract Free
                                </Link>

                                <Link
                                    to="/examples"
                                    className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-6 py-3.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                                >
                                    View Example Deployments
                                </Link>
                            </div>

                            <div className="mt-4 flex flex-wrap gap-3 text-xs font-medium text-slate-500">
                                <span>No credit card required</span>
                                <span>•</span>
                                <span>Wallet-confirmed transactions</span>
                                <span>•</span>
                                <span>Gas and network fees stay in your wallet</span>
                            </div>

                            <div className="mt-10 grid max-w-2xl gap-3 sm:grid-cols-3">
                                {[
                                    ["Under 10 minutes", "From wallet connect to verified mainnet contract. No setup."],
                                    ["Zero custody", "Your wallet signs and pays. Krionex never holds your funds."],
                                    ["Auto-verified", "Explorer verification happens automatically after every deploy."],
                                ].map(([title, desc]) => (
                                    <div
                                        key={title}
                                        className="rounded-2xl border border-[#E6C978]/30 bg-white p-5 shadow-[0_14px_35px_rgba(197,155,69,0.08)]"
                                    >
                                        <div className="text-sm font-semibold text-[#050713]">
                                            {title}
                                        </div>
                                        <div className="mt-2 text-xs leading-5 text-slate-500">
                                            {desc}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-10">
                                <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                                    Supported EVM networks
                                </div>
                                <div className="flex flex-wrap gap-3">
                                    {NETWORK_ICONS.map((network) => (
                                        <div
                                            key={network.name}
                                            className="inline-flex items-center gap-2 rounded-full border border-[#E6C978]/30 bg-white px-3 py-2 shadow-[0_8px_24px_rgba(15,23,42,0.05)]"
                                        >
                                            <img
                                                src={network.logo}
                                                alt={network.name}
                                                className="h-4 w-4 rounded-full object-contain"
                                            />
                                            <span className="text-xs font-medium text-slate-600">
                                                {network.name}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="relative">
                            <div className="absolute -inset-8 rounded-[2.5rem] bg-[radial-gradient(circle_at_30%_15%,rgba(197,155,69,0.22),transparent_32%),radial-gradient(circle_at_80%_80%,rgba(15,23,42,0.16),transparent_34%)] blur-2xl" />

                            <div className="relative rounded-[2rem] border border-slate-200/80 bg-white p-3 shadow-[0_35px_100px_rgba(15,23,42,0.16)]">
                                <div className="overflow-hidden rounded-[1.45rem] border border-slate-200 bg-white">
                                    <div className="flex items-center justify-between border-b border-slate-800 bg-[#050713] px-5 py-4 text-white">
                                        <div className="flex items-center gap-3">
                                            <img
                                                src="/logos/krionex-icon.png"
                                                alt="Krionex"
                                                className="h-12 w-12 rounded-2xl object-contain"
                                            />
                                            <div>
                                                <div className="text-sm font-semibold">
                                                    Workspace preview
                                                </div>
                                                <div className="text-xs text-slate-400">
                                                    Launch · Manage · Monitor
                                                </div>
                                            </div>
                                        </div>

                                        <div className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-[11px] font-semibold text-emerald-200">
                                            Ready
                                        </div>
                                    </div>

                                    <div className="grid gap-3 bg-[#FBFCFE] p-5">
                                        {[
                                            ["Contract setup", "ERC20 / ERC721 / ERC1155", "Configured"],
                                            ["Wallet confirmation", "User-controlled approval flow", "Secure"],
                                            ["Deployment record", "Explorer links and history", "Tracked"],
                                            ["Operations", "Minting and post-launch actions", "Active"],
                                        ].map(([title, desc, status]) => (
                                            <div
                                                key={title}
                                                className="rounded-2xl border border-slate-200 bg-white p-4"
                                            >
                                                <div className="flex items-center justify-between gap-4">
                                                    <div>
                                                        <div className="text-sm font-semibold text-[#050713]">
                                                            {title}
                                                        </div>
                                                        <div className="mt-1 text-xs leading-5 text-slate-500">
                                                            {desc}
                                                        </div>
                                                    </div>
                                                    <div className="rounded-full border border-[#E8D7AD] bg-[#FFF9EA] px-3 py-1 text-[11px] font-semibold text-[#9A7226]">
                                                        {status}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        <div className="rounded-2xl border border-slate-200 bg-[#050713] p-5 text-white">
                                            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#E6C978]">
                                                Active flow
                                            </div>
                                            <div className="mt-2 text-base font-semibold">
                                                Configure → Confirm → Deploy → Operate
                                            </div>
                                            <div className="mt-4 h-2 rounded-full bg-white/10">
                                                <div className="h-2 w-3/4 rounded-full bg-gradient-to-r from-[#F4D47A] via-[#C59B45] to-[#8C6722]" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="mx-auto max-w-7xl px-6 pb-16">
                    <div className="rounded-[2rem] border border-[#E6C978]/40 bg-white p-8 shadow-[0_18px_55px_rgba(15,23,42,0.05)]">
                        <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#A87E2C]">
                            Try Krionex if you are
                        </div>

                        <div className="mt-6 grid gap-4 md:grid-cols-3">
                            {[
                                [
                                    "Launching a token for the first time",
                                    "Configure your ERC20 in a simple form, confirm from your wallet, and get a verified contract live on mainnet. No environment setup.",
                                ],
                                [
                                    "Building an NFT or multi-token project",
                                    "Deploy ERC721 or ERC1155 contracts with wallet confirmation, automatic verification, and deployment records — without writing a single deployment script.",
                                ],
                                [
                                    "Deploying contracts for clients",
                                    "Every deployment generates automatic records with contract address, transaction hash, and explorer links — ready to hand off to your client.",
                                ],
                            ].map(([title, desc]) => (
                                <div
                                    key={title}
                                    className="rounded-2xl border border-slate-200 bg-[#FBFCFE] p-5"
                                >
                                    <div className="text-sm font-semibold text-[#050713]">
                                        {title}
                                    </div>
                                    <p className="mt-2 text-sm leading-6 text-slate-600">
                                        {desc}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section id="platform" className="border-y border-slate-200/80 bg-white/65 px-6 py-16 backdrop-blur">
                    <div className="mx-auto max-w-7xl">
                        <SectionHeader
                            eyebrow="Platform"
                            title="Deploy, verify, and operate contracts with context."
                            subtitle="Krionex keeps contract setup, wallet confirmation, explorer verification, deployment records, and post-launch actions in one focused workspace."
                            center
                        />

                        <div className="mt-10 grid gap-5 md:grid-cols-3">
                            {CAPABILITIES.map((item) => (
                                <div
                                    key={item.title}
                                    className="rounded-3xl border border-slate-200 bg-white p-7 shadow-[0_14px_40px_rgba(15,23,42,0.04)]"
                                >
                                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A87E2C]">
                                        {item.title}
                                    </div>
                                    <h3 className="mt-5 text-2xl font-semibold tracking-[-0.035em] text-[#050713]">
                                        {item.title} contracts with context.
                                    </h3>
                                    <p className="mt-4 text-sm leading-7 text-slate-600">
                                        {item.desc}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section id="workflow" className="mx-auto max-w-7xl px-6 py-20">
                    <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr]">
                        <SectionHeader
                            eyebrow="Workflow"
                            title="Built for repeat usage, not one-off demos."
                            subtitle="Move from setup to deployment to operations without losing contract context."
                        />

                        <div className="grid gap-4 sm:grid-cols-2">
                            {WORKFLOW.map((item) => (
                                <div
                                    key={item.step}
                                    className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_12px_35px_rgba(15,23,42,0.04)]"
                                >
                                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A87E2C]">
                                        {item.step}
                                    </div>
                                    <div className="mt-5 text-lg font-semibold text-[#050713]">
                                        {item.title}
                                    </div>
                                    <p className="mt-3 text-sm leading-7 text-slate-600">
                                        {item.desc}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section id="security" className="border-y border-slate-200/80 bg-white/70 px-6 py-20 backdrop-blur">
                    <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[0.95fr_1.05fr]">
                        <div className="rounded-[2rem] bg-[#050713] p-8 text-white shadow-[0_30px_80px_rgba(15,23,42,0.22)] md:p-10">
                            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E6C978]">
                                User-controlled execution
                            </div>
                            <h2 className="mt-5 text-3xl font-semibold tracking-[-0.04em] sm:text-4xl">
                                Krionex prepares. You approve. Nothing moves without you.
                            </h2>
                            <p className="mt-5 text-sm leading-8 text-slate-300">
                                Every deployment and token action is a wallet-confirmed transaction. Krionex prepares the transaction details — you review and sign from your own wallet. Gas fees go directly to the network. No funds pass through Krionex. No audit of Krionex is required because we never custody, hold, or move your assets.
                            </p>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            {TRUST_POINTS.map((item) => (
                                <div
                                    key={item.title}
                                    className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_12px_35px_rgba(15,23,42,0.04)]"
                                >
                                    <div className="text-sm font-semibold text-[#050713]">
                                        {item.title}
                                    </div>
                                    <p className="mt-3 text-sm leading-7 text-slate-600">
                                        {item.desc}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="mx-auto max-w-7xl px-6 py-16">
                    <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_18px_55px_rgba(15,23,42,0.05)] md:p-10">
                        <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#A87E2C]">
                            How Krionex works with your money
                        </div>
                        <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] text-[#050713] sm:text-4xl">
                            We never touch your funds. Ever.
                        </h2>
                        <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600">
                            Krionex is a workflow tool, not a financial service. Here is exactly what happens with your wallet and your money.
                        </p>

                        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {[
                                [
                                    "You pay gas. Not us.",
                                    "The only cost to deploy a contract is the network gas fee. That fee goes directly from your wallet to the blockchain network. Krionex never receives it, never holds it, and never charges a percentage of it.",
                                ],
                                [
                                    "Your wallet signs everything.",
                                    "Every action — deployment, minting, pausing — is a transaction signed directly from your connected wallet. Krionex prepares the transaction. You approve it. Nothing happens without your explicit confirmation.",
                                ],
                                [
                                    "No private key access.",
                                    "Krionex never asks for your private key, seed phrase, or any wallet credentials. We connect to your wallet through the standard browser wallet interface the same way any Web3 site does.",
                                ],
                                [
                                    "No smart contract audit required.",
                                    "Krionex does not custody funds, does not hold tokens, and does not execute financial logic on your behalf. We are a deployment interface — not a DeFi protocol.",
                                ],
                                [
                                    "Free plan requires no payment.",
                                    "Your first deployment is completely free. No credit card. No wallet charge from Krionex. The only thing you need is gas on your target network.",
                                ],
                                [
                                    "You own your contracts.",
                                    "Every contract you deploy through Krionex is owned by your wallet address. Krionex has no admin access, no ownership role, and no ability to interact with your contract after deployment.",
                                ],
                            ].map(([title, desc]) => (
                                <div
                                    key={title}
                                    className="rounded-2xl border border-slate-200 bg-[#FBFCFE] p-5"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="mt-1 text-emerald-500 text-base">✓</div>
                                        <div>
                                            <div className="text-sm font-semibold text-[#050713]">
                                                {title}
                                            </div>
                                            <p className="mt-2 text-xs leading-5 text-slate-500">
                                                {desc}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="mx-auto max-w-7xl px-6 pb-6">
                    <div className="rounded-[2rem] bg-[#050713] p-8 text-white shadow-[0_30px_80px_rgba(15,23,42,0.20)] md:p-10">
                        <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E6C978]">
                            Early access
                        </div>

                        <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-4xl">
                            Deploy your first contract free. No credit card. No setup. No surprises.
                        </h2>

                        <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-300">
                            Connect your wallet and deploy a real ERC20, ERC721, or ERC1155 contract to mainnet in under 10 minutes. Krionex is fully browser-based — nothing installs on your machine. You sign and pay gas directly from your own wallet. We never ask for funds, never hold your assets, and never access your private keys.
                        </p>

                        <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                            <Link
                                to="/studio"
                                className="inline-flex items-center justify-center rounded-xl bg-white px-5 py-3 text-sm font-semibold text-[#050713] transition hover:bg-slate-100"
                            >
                                Try Deployment Free
                            </Link>

                            <Link
                                to="/contact"
                                className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/15"
                            >
                                Contact for Demo
                            </Link>
                        </div>
                    </div>
                </section>

                <section id="pricing" className="mx-auto max-w-7xl px-6 py-20">
                    <SectionHeader
                        eyebrow="Pricing"
                        title="Start with free deployment. Upgrade when your workflow grows."
                        subtitle="Use the free plan to try the deployment workflow across supported networks. Move to Pro when you need higher limits, operations, analytics, and active contract management."
                        center
                    />

                    <div className="mt-10 grid gap-5 lg:grid-cols-2">
                        <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_18px_55px_rgba(15,23,42,0.05)]">
                            <div className="text-sm font-semibold text-slate-700">Free</div>
                            <h3 className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-[#050713]">
                                Try contract deployment free
                            </h3>
                            <p className="mt-3 text-sm leading-7 text-slate-600">
                                Start with wallet-confirmed deployment and see how Krionex organizes contract setup, network selection, deployment records, and history.
                            </p>

                            <ul className="mt-6 space-y-3">
                                <CheckItem>Deploy from your connected wallet</CheckItem>
                                <CheckItem>Choose supported EVM networks</CheckItem>
                                <CheckItem>Track deployment records and history</CheckItem>
                                <CheckItem>Best for first deployment tests and evaluation</CheckItem>
                            </ul>

                            <div className="mt-8">
                                <Link
                                    to="/studio"
                                    className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50"
                                >
                                    Deploy Free
                                </Link>
                            </div>
                        </div>

                        <div className="relative overflow-hidden rounded-[2rem] border border-[#E8D7AD] bg-[#FFFDF8] p-8 shadow-[0_24px_70px_rgba(197,155,69,0.13)]">
                            <div className="absolute -right-24 -top-24 h-60 w-60 rounded-full bg-[#E6C978]/25 blur-3xl" />

                            <div className="relative">
                                <div className="inline-flex rounded-full border border-[#E8D7AD] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9A7226] shadow-sm">
                                    Recommended
                                </div>

                                <div className="mt-5 text-sm font-semibold text-[#9A7226]">
                                    Pro
                                </div>
                                <h3 className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-[#050713]">
                                    Unlock active contract operations
                                </h3>
                                <p className="mt-3 text-sm leading-7 text-slate-700">
                                    Use Pro when you need ERC721/ERC1155 support, higher deployment and preflight limits, token operations, analytics, and liquidity visibility.
                                </p>

                                <ul className="mt-6 space-y-3">
                                    <CheckItem>ERC20, ERC721, and ERC1155 support</CheckItem>
                                    <CheckItem>Expanded deployment limits</CheckItem>
                                    <CheckItem>Operations and mint workflows</CheckItem>
                                    <CheckItem>Better fit for active builders and teams</CheckItem>
                                </ul>

                                <div className="mt-8">
                                    <Link
                                        to="/upgrade"
                                        className="inline-flex items-center justify-center rounded-xl bg-[#050713] px-5 py-3 text-sm font-semibold text-white shadow-[0_16px_42px_rgba(15,23,42,0.18)] transition hover:bg-slate-900"
                                    >
                                        View Pro plan
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="mx-auto max-w-7xl px-6 pb-20">
                    <div className="relative overflow-hidden rounded-[2rem] bg-[#050713] p-8 text-white shadow-[0_32px_100px_rgba(15,23,42,0.22)] md:p-10">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(230,201,120,0.22),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.08),transparent_30%)]" />
                        <div className="relative flex flex-col gap-7 md:flex-row md:items-center md:justify-between">
                            <div className="max-w-2xl">
                                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E6C978]">
                                    Ready to deploy
                                </div>
                                <h2 className="mt-3 text-3xl font-semibold tracking-[-0.04em] sm:text-4xl">
                                    Deploy your next smart contract from one workspace.
                                </h2>
                                <p className="mt-4 text-sm leading-7 text-slate-300">
                                    Browser-based. Non-custodial. Nothing installs on your machine. Connect your wallet and deploy your first contract free — you control every transaction, every approval, and every fee. Krionex never holds your funds or accesses your keys.
                                </p>
                            </div>

                            <div className="flex flex-col gap-3 sm:flex-row">
                                <Link
                                    to="/studio"
                                    className="inline-flex items-center justify-center rounded-xl bg-white px-5 py-3 text-sm font-semibold text-[#050713] transition hover:bg-slate-100"
                                >
                                    Deploy Free
                                </Link>
                                <Link
                                    to="/upgrade"
                                    className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/15"
                                >
                                    View pricing
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="relative z-10 border-t border-slate-200/80 bg-white px-6 py-10 text-sm text-slate-500">
                <div className="mx-auto flex max-w-7xl flex-col gap-8 md:flex-row md:items-center md:justify-between">
                    <div>
                        <KrionexLogo
                            variant="full"
                            to="/"
                            imageClassName="h-20 w-auto object-contain"
                        />
                        <div className="mt-4 text-xs leading-5 text-slate-500">
                            © {new Date().getFullYear()} KRIONEX. Browser-based smart contract deployment. Non-custodial. No funds held.
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-5 text-sm font-medium">
                        <Link to="/terms" className="transition hover:text-[#050713]">
                            Terms
                        </Link>
                        <Link to="/privacy" className="transition hover:text-[#050713]">
                            Privacy
                        </Link>
                        <Link to="/refund-policy" className="transition hover:text-[#050713]">
                            Refund Policy
                        </Link>
                        <Link to="/contact" className="transition hover:text-[#050713]">
                            Contact
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}